import React, { useEffect, useState } from 'react';
import { Icon } from '@tt/ui';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { CarouselProvider, Slide, Slider } from 'pure-react-carousel';

import BenefitItemsMobileMeteredExperiment from './BenefitItemsMobile/BenefitItemsMobileMeteredExperiment';
import BenefitSectionMetered from './BenefitSection/BenefitSectionMetered';
import { PackageHeaderMeteredExperiment } from './PackageHeader';

import 'pure-react-carousel/dist/react-carousel.es.css';
import styles from './BenefitsViewMetered.module.scss';

const BenefitsViewMetered = ({
  benefits,
  displayMonthlyPrice = false,
  essentialsPrice,
  proMeteredPrice,
  unlimitedPrice,
  onGetEssentials,
  onGetProMetered,
  onGetUnlimited,
  preAppliedCoupon,
  isMobile = false,
  selectedBenefit,
  onClickSegment,
  extraParams: parentExtraParams,
}) => {
  const [currentPlan, setCurrentPlan] = useState(0);
  const [slidePosition, setSlidePosition] = useState(31);
  const totalSlides = 60;

  useEffect(() => {
    const planIndex = ((slidePosition % 3) + 3) % 3;
    setCurrentPlan(planIndex);
  }, [slidePosition]);

  const handleNextClick = () => setSlidePosition((prev) => prev + 1);
  const handlePrevClick = () => setSlidePosition((prev) => prev - 1);

  const handleBenefitSelect = (benefitKey) => {
    if (onClickSegment) {
      onClickSegment(`${benefitKey}_tab`);
    }
  };

  const extraParams = {
    ...parentExtraParams,
    onClickCTA: handleBenefitSelect,
  };

  const renderMobileHeader = (slideIndex) => {
    const planType = slideIndex % 3;

    let title,
      price,
      buttonText,
      onButtonClick,
      bestValue = false;

    if (planType === 0) {
      title = 'Essentials';
      price = essentialsPrice;
      buttonText = 'GET ESSENTIALS';
      onButtonClick = onGetEssentials;
    } else if (planType === 1) {
      title = 'Pro';
      price = proMeteredPrice;
      buttonText = 'GET PRO';
      onButtonClick = onGetProMetered;
      bestValue = true;
    } else {
      title = 'Unlimited';
      price = unlimitedPrice;
      buttonText = 'GET UNLIMITED';
      onButtonClick = onGetUnlimited;
    }

    const cardClasses = cx(styles.packageHeader, {
      [styles.essentials]: planType === 0,
      [styles.pro]: planType === 1,
      [styles.unlimited]: planType === 2,
    });

    return (
      <div className={cardClasses}>
        {bestValue && <div className={styles.bestValueTag}>Most Popular</div>}
        <div className={styles.headerContent}>
          <h3 className={styles.packageTitle}>{title}</h3>
          <div className={styles.priceWrapper}>
            <span className={styles.dollarSign}>$</span>
            <span className={styles.price}>{price}</span>
            <span className={styles.period}>PER YEAR</span>
          </div>
          <button
            onClick={onButtonClick}
            className={cx(styles.actionButton, {
              [styles.primaryButton]: planType === 1,
              [styles.secondaryButton]: planType !== 1,
            })}
          >
            {buttonText}
          </button>
        </div>
      </div>
    );
  };

  return isMobile ? (
    <div className={styles.mobileSectionWrapper}>
      <CarouselProvider
        naturalSlideWidth={200}
        naturalSlideHeight={150}
        totalSlides={totalSlides}
        visibleSlides={1}
        step={1}
        dragEnabled={true}
        touchEnabled={true}
        currentSlide={slidePosition}
        className={styles.carouselContainer}
        isIntrinsicHeight={true}
        classNameTray={styles.carouselTray}
        classNameTrayWrap={styles.carouselTrayWrap}
        classNameSlider={styles.carouselSlider}
      >
        <div className={styles.carouselWrapper}>
          <button
            className={cx(styles.carouselArrow, styles.prevArrow)}
            onClick={handlePrevClick}
          >
            <Icon name="chevron-arrow-left" size="xxl" />
          </button>

          <Slider className={styles.carousel}>
            {Array.from({ length: totalSlides }).map((_, index) => (
              <Slide key={index} index={index} className={styles.slide}>
                {renderMobileHeader(index)}
              </Slide>
            ))}
          </Slider>

          <button
            className={cx(styles.carouselArrow, styles.nextArrow)}
            onClick={handleNextClick}
          >
            <Icon name="chevron-arrow-right" size="xxl" />
          </button>
        </div>
      </CarouselProvider>

      <div className={styles.mobileContent}>
        <BenefitItemsMobileMeteredExperiment
          benefits={benefits}
          selectedBenefit={selectedBenefit}
          extraParams={extraParams}
          isEssentialsPlan={currentPlan === 0}
          isProPlan={currentPlan === 1}
          isPremiumPlan={currentPlan === 2}
          onClickSegment={onClickSegment}
          showEmptyBenefits={true}
          displayAllBenefits={true}
        />
      </div>
    </div>
  ) : (
    <>
      <div className={cx(styles.headerRowMetered, styles[selectedBenefit])}>
        <div className={styles.emptyHeader} />
        <PackageHeaderMeteredExperiment
          title="Essentials"
          price={essentialsPrice}
          displayMonthlyPrice={displayMonthlyPrice}
          onButtonClick={onGetEssentials}
          preAppliedCoupon={preAppliedCoupon}
        />
        <PackageHeaderMeteredExperiment
          title="Pro"
          price={proMeteredPrice}
          displayMonthlyPrice={displayMonthlyPrice}
          onButtonClick={onGetProMetered}
          bestValue={true}
          preAppliedCoupon={preAppliedCoupon}
        />
        <PackageHeaderMeteredExperiment
          title="Unlimited"
          price={unlimitedPrice}
          displayMonthlyPrice={displayMonthlyPrice}
          onButtonClick={onGetUnlimited}
          preAppliedCoupon={preAppliedCoupon}
        />
      </div>
      <div className={styles.allBenefits}>
        <BenefitSectionMetered
          benefits={benefits}
          selectedBenefit={selectedBenefit}
          extraParams={extraParams}
        />
      </div>
    </>
  );
};

BenefitsViewMetered.propTypes = {
  benefits: PropTypes.array.isRequired,
  displayMonthlyPrice: PropTypes.bool.isRequired,
  onGetEssentials: PropTypes.func.isRequired,
  onGetProMetered: PropTypes.func.isRequired,
  onGetUnlimited: PropTypes.func.isRequired,
  essentialsPrice: PropTypes.number.isRequired,
  proMeteredPrice: PropTypes.number.isRequired,
  unlimitedPrice: PropTypes.number.isRequired,
  preAppliedCoupon: PropTypes.object,
  isMobile: PropTypes.bool,
  selectedBenefit: PropTypes.string.isRequired,
  onClickSegment: PropTypes.func,
  extraParams: PropTypes.object,
};

export default BenefitsViewMetered;
